import { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy, Link as LinkIcon } from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import type { PublicClientApplication } from '@azure/msal-browser';
import { createIntakeLink } from '../../services/intakeService';
import { getClients } from '../../services/clientService';
import { searchGal, fetchUserPhoto, type GalUser } from '../../services/graphService';
import type { Client } from '../../types/project';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a link is created so the list can refresh. */
  onCreated?: () => void;
}

const EXPIRY_OPTIONS = [7, 14, 30, 60, 90];

/**
 * Mint a tokenized intake link (the replacement for emailing the KeyLAB
 * workbook). The lab optionally pre-fills the client/contact, picks an
 * expiry, and copies the URL into their email. Authenticated lab users can
 * also re-copy the link later from the Intake Requests list.
 */
export const CreateIntakeLinkDialog = ({ open, onClose, onCreated }: Props) => {
  const { instance } = useMsal();
  const [clients, setClients] = useState<Client[]>([]);
  const [client, setClient] = useState<Client | null>(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [projectName, setProjectName] = useState('');
  const [expiryDays, setExpiryDays] = useState(30);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // GAL lookup for the contact (same directory search as Admin → Users).
  const [galResults, setGalResults] = useState<GalUser[]>([]);
  const [galLoading, setGalLoading] = useState(false);
  const [galPhotos, setGalPhotos] = useState<Record<string, string | null>>({});
  const galTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    setCreatedUrl(null);
    setCopied(false);
    setError(null);
    setGalResults([]);
    getClients().then(setClients).catch(() => setClients([]));
  }, [open]);

  const searchContacts = (query: string) => {
    setContactName(query);
    if (galTimer.current) clearTimeout(galTimer.current);
    if (query.trim().length < 2) { setGalResults([]); return; }
    galTimer.current = setTimeout(async () => {
      setGalLoading(true);
      try {
        const results = await searchGal(instance as unknown as PublicClientApplication, query.trim());
        setGalResults(results);
        // Photos are best-effort, loaded per result in the background.
        for (const u of results.slice(0, 8)) {
          const id = u.mail ?? u.userPrincipalName;
          if (id && !(id in galPhotos)) {
            fetchUserPhoto(instance as unknown as PublicClientApplication, u.id)
              .then((url) => setGalPhotos((prev) => ({ ...prev, [id]: url })));
          }
        }
      } catch {
        setGalResults([]);
      } finally {
        setGalLoading(false);
      }
    }, 350);
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const { token } = await createIntakeLink({
        clientKey: client?.ClientKey ?? null,
        clientName: client?.ClientName ?? null,
        contactName: contactName.trim() || null,
        contactEmail: contactEmail.trim() || null,
        projectName: projectName.trim() || null,
        expiryDays,
      });
      setCreatedUrl(`${window.location.origin}/intake/${token}`);
      onCreated?.();
    } catch (err) {
      setError((err as Error).message || 'Failed to create the link.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdUrl) return;
    await navigator.clipboard.writeText(createdUrl);
    setCopied(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinkIcon fontSize="small" /> New Intake Link
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {createdUrl ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Link created — copy it now and send it to the requester (project team or client). Expires in {expiryDays} days.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField value={createdUrl} fullWidth size="small" InputProps={{ readOnly: true }} />
              <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              The requester fills the form at this link — it autosaves, and they submit when ready.
              You'll find the submission under Intake Requests.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Optionally pre-fill what you know — the requester can complete the rest.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <Autocomplete
              options={clients}
              getOptionLabel={(c) => c.ClientName ?? ''}
              value={client}
              onChange={(_, v) => setClient(v)}
              renderInput={(params) => (
                <TextField {...params} label="Client (optional)" size="small" />
              )}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Autocomplete
                freeSolo
                options={galResults}
                loading={galLoading}
                inputValue={contactName}
                filterOptions={(x) => x}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.displayName)}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input' || reason === 'clear') searchContacts(value);
                }}
                onChange={(_, value) => {
                  if (value && typeof value !== 'string') {
                    setContactName(value.displayName);
                    setContactEmail(value.mail ?? value.userPrincipalName ?? '');
                    setGalResults([]);
                  }
                }}
                renderOption={(props, o) => {
                  const id = o.mail ?? o.userPrincipalName;
                  return (
                    <Box component="li" {...props} key={o.id} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                      <Avatar src={galPhotos[id] ?? undefined} sx={{ width: 28, height: 28, fontSize: 13 }}>
                        {o.displayName?.[0] ?? '?'}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{o.displayName}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {[o.mail ?? o.userPrincipalName, o.jobTitle].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Contact name (optional)" size="small"
                    helperText="Type to search the directory — or enter any name" />
                )}
              />
              <TextField label="Contact email (optional)" size="small" value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)} />
            </Box>
            <TextField label="Project name (optional)" size="small" value={projectName}
              onChange={(e) => setProjectName(e.target.value)} />
            <TextField label="Link expires after" size="small" select value={expiryDays}
              onChange={(e) => setExpiryDays(Number(e.target.value))} sx={{ width: 200 }}>
              {EXPIRY_OPTIONS.map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
            </TextField>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {createdUrl ? 'Done' : 'Cancel'}
        </Button>
        {!createdUrl && (
          <Button variant="contained" onClick={handleCreate} disabled={creating}
            sx={{ textTransform: 'none' }}>
            {creating ? 'Creating…' : 'Create link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
